import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1709548145082-04d0cde481d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data);
      toast.success("Bentornato!");
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-gray-900">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/"><Logo onDark /></Link>
          <div>
            <h2 className="font-display font-bold text-4xl leading-tight mb-3 text-white">Bentornato su MenuOne</h2>
            <p className="text-white/80 max-w-sm">Gestisci il tuo menù digitale, aggiorna piatti e prezzi in un istante.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm mo-fade-up">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="font-display font-bold text-4xl mb-2">Accedi</h1>
          <p className="text-gray-500 mb-8">Entra nel tuo account MenuOne.</p>

          <Button onClick={googleLogin} variant="outline" className="w-full h-11 rounded-full border-gray-300 bg-white hover:bg-gray-50 mb-5" data-testid="google-login-btn">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5 mr-2" /> Continua con Google
          </Button>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-gray-200" /><span className="text-xs text-gray-400 uppercase tracking-wider">oppure</span><div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="tua@email.it" data-testid="login-email" />
            </div>
            <div>
            <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#5aa469] font-medium hover:underline" data-testid="forgot-password-link">Password dimenticata?</Link>
              </div>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="••••••••" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="login-submit">
              {loading ? "Accesso…" : "Accedi"}
            </Button>
          </form>
          <p className="text-sm text-gray-500 mt-6 text-center">
            Non hai un account? <Link to="/register" className="text-[#5aa469] font-medium hover:underline" data-testid="to-register">Registrati</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
